import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CRMPermissionsService } from "@/services/crm-permissions";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface FieldsConfigModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentFields: string[];
    allFields: string[];
    onFieldsChange: (fields: string[]) => void;
    profile: string;
    referenceTypeId: number;
}

export function FieldsConfigModal({ open, onOpenChange, currentFields, allFields, onFieldsChange, profile, referenceTypeId }: FieldsConfigModalProps) {
    const [searchField, setSearchField] = useState("");
    const [tempFields, setTempFields] = useState<string[]>(currentFields);
    const [allFieldsVisible, setAllFieldsVisible] = useState(false);
    const [availableProfiles, setAvailableProfiles] = useState<string[]>([]);
    const [selectedSourceProfile, setSelectedSourceProfile] = useState<string>("");
    const [copyLoading, setCopyLoading] = useState(false);

    // Reset temp state when modal opens or currentFields prop changes
    useEffect(() => {
        setTempFields(currentFields);
        setAllFieldsVisible(currentFields.includes("*"));
    }, [currentFields, open]);

    // Récupérer les profils disponibles pour la copie
    useEffect(() => {
        if (open) {
            const fetchAvailableProfiles = async () => {
                try {
                    const permissions = await CRMPermissionsService.getPermissions(referenceTypeId);
                    const profiles = permissions
                        .map(p => p.profile)
                        .filter(profileName => profileName !== profile) // Exclure le profil actuel
                        .sort();
                    setAvailableProfiles(profiles);
                } catch (error) {
                    console.error("Error fetching available profiles:", error);
                }
            };
            fetchAvailableProfiles();
        }
    }, [open, referenceTypeId, profile]);

    // Fonction pour basculer la visibilité d'un champ
    const toggleFieldVisibility = (field: string) => {
        if (allFieldsVisible) {
            // Si tous les champs sont visibles, on passe en mode sélectif
            setAllFieldsVisible(false);
            setTempFields([field]);
        } else {
            const newFields = tempFields.includes(field) ? tempFields.filter(f => f !== field) : [...tempFields, field];
            setTempFields(newFields);
        }
    };

    // Fonction pour activer "tous les champs"
    const enableAllFields = () => {
        setAllFieldsVisible(true);
        setTempFields(["*"]);
    };

    // Fonction pour désactiver "tous les champs"
    const disableAllFields = () => {
        setAllFieldsVisible(false);
        setTempFields([]);
    };

    // Handle validate button click
    const handleValidate = () => {
        onFieldsChange(tempFields);
        onOpenChange(false);
    };

    // Handle cancel button click
    const handleCancel = () => {
        setTempFields(currentFields);
        setAllFieldsVisible(currentFields.includes("*"));
        onOpenChange(false);
    };

    // Fonction pour copier les champs d'un autre profil
    const handleCopyFields = async () => {
        if (!selectedSourceProfile) {
            toast.error("Please select a source profile");
            return;
        }

        setCopyLoading(true);

        try {
            const permissions = await CRMPermissionsService.getPermissions(referenceTypeId);
            const sourcePermission = permissions.find(p => p.profile === selectedSourceProfile);

            if (!sourcePermission) {
                toast.error("Source profile not found");
                return;
            }

            // Mettre à jour les champs temporaires avec ceux du profil source
            const sourceFields = sourcePermission.fields || [];
            setTempFields(sourceFields);
            setAllFieldsVisible(sourceFields.includes("*"));

            toast.success(`Fields copied from the profile "${selectedSourceProfile}"`);
            setSelectedSourceProfile(""); // Réinitialiser la sélection
        } catch (error) {
            toast.error("Error copying fields");
        } finally {
            setCopyLoading(false);
        }
    };

    // Filtrage des champs selon la recherche
    const filteredFields = allFields.filter(field => field.toLowerCase().includes(searchField.toLowerCase()));

    // Séparation des champs directs et des champs d'objets liés
    const directFields = filteredFields.filter(field => !field.includes("."));
    const relatedFields = filteredFields.filter(field => field.includes("."));

    // Groupement des champs d'objets liés par relation
    const relatedFieldsGrouped = relatedFields.reduce(
        (acc, field) => {
            const [relationName] = field.split(".");
            if (!acc[relationName]) {
                acc[relationName] = [];
            }
            acc[relationName].push(field);
            return acc;
        },
        {} as Record<string, string[]>
    );

    // Calcul du nombre de champs visibles
    const visibleFieldsCount = allFieldsVisible ? allFields.length : tempFields.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] w-full max-w-4xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Visible fields config.</DialogTitle>
                </DialogHeader>
                <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-col gap-4 py-4">
                        {/* Section de copie des champs */}
                        {availableProfiles.length > 0 && (
                            <div className="rounded-lg border bg-gray-50 p-4">
                                <h3 className="mb-3 text-sm font-medium text-gray-900">Copy fields from another profile</h3>
                                <div className="flex gap-2">
                                    <Select value={selectedSourceProfile} onValueChange={setSelectedSourceProfile}>
                                        <SelectTrigger className="w-64">
                                            <SelectValue placeholder="Select a source profile" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableProfiles.map(profileName => (
                                                <SelectItem key={profileName} value={profileName}>
                                                    {profileName}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button type="button" variant="outline" onClick={handleCopyFields} disabled={!selectedSourceProfile || copyLoading} className="whitespace-nowrap">
                                        {copyLoading ? "Copy..." : "Copy fields"}
                                    </Button>
                                </div>
                            </div>
                        )}
                        {/* Option "Tous les champs" */}
                        <div className="flex w-full items-center space-x-3 rounded border p-4">
                            <Checkbox
                                checked={allFieldsVisible}
                                onCheckedChange={(checked: boolean) => {
                                    if (checked) {
                                        enableAllFields();
                                    } else {
                                        disableAllFields();
                                    }
                                }}
                            />
                            <div className="flex w-full flex-1 flex-col gap-1">
                                <div className="flex w-full items-center space-x-2">
                                    <span className="text-sm font-medium">All fields</span>
                                    <Badge variant="outline">*</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">Allow access to all fields available ({allFields.length} fields)</p>
                            </div>
                        </div>

                        {/* Séparateur */}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background px-2 text-muted-foreground">Or select specific fields</span>
                            </div>
                        </div>

                        {/* Recherche */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                            <Input
                                placeholder="Search a field..."
                                value={searchField}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchField(e.target.value)}
                                className="pl-10"
                                disabled={allFieldsVisible}
                            />
                        </div>

                        {/* Liste des champs */}
                        <ScrollArea className="h-[30dvh] rounded-md border p-4">
                            <div className="space-y-4">
                                {/* Section des champs directs */}
                                {directFields.length > 0 && (
                                    <div>
                                        <div className="mb-3 flex items-center gap-2">
                                            <h4 className="text-sm font-semibold text-gray-700">Direct fields</h4>
                                            <Badge variant="outline" className="text-xs">
                                                {directFields.length}
                                            </Badge>
                                        </div>
                                        <div className="space-y-1">
                                            {directFields
                                                .sort((a, b) => a.localeCompare(b))
                                                .map(field => (
                                                    <div key={field} className={cn("flex items-center space-x-2 rounded p-2 hover:bg-gray-50", allFieldsVisible && "opacity-50")}>
                                                        <Checkbox
                                                            checked={allFieldsVisible || tempFields.includes(field)}
                                                            onCheckedChange={() => toggleFieldVisibility(field)}
                                                            disabled={allFieldsVisible}
                                                        />
                                                        <div className="min-w-0 flex-1 break-all">
                                                            <span className="text-sm font-medium">{field}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}

                                {/* Section des champs d'objets liés */}
                                {Object.keys(relatedFieldsGrouped).length > 0 && (
                                    <div>
                                        <div className="mb-3 flex items-center gap-2">
                                            <h4 className="text-sm font-semibold text-gray-700">Related object fields</h4>
                                            <Badge variant="outline" className="text-xs">
                                                {relatedFields.length}
                                            </Badge>
                                        </div>
                                        <div className="space-y-3">
                                            {Object.entries(relatedFieldsGrouped)
                                                .sort(([a], [b]) => a.localeCompare(b))
                                                .map(([relationName, fields]) => (
                                                    <div key={relationName} className="rounded border bg-gray-50/50 p-3">
                                                        <div className="mb-2 flex items-center gap-2">
                                                            <span className="text-xs font-medium text-gray-600">{relationName}</span>
                                                            <Badge variant="secondary" className="text-xs">
                                                                {fields.length}
                                                            </Badge>
                                                        </div>
                                                        <div className="space-y-1">
                                                            {fields
                                                                .sort((a, b) => a.localeCompare(b))
                                                                .map(field => (
                                                                    <div key={field} className={cn("flex items-center space-x-2 rounded p-2 hover:bg-white/50", allFieldsVisible && "opacity-50")}>
                                                                        <Checkbox
                                                                            checked={allFieldsVisible || tempFields.includes(field)}
                                                                            onCheckedChange={() => toggleFieldVisibility(field)}
                                                                            disabled={allFieldsVisible}
                                                                        />
                                                                        <div className="min-w-0 flex-1 break-all">
                                                                            <span className="text-sm">{field.split(".").slice(1).join(".")}</span>
                                                                            <span className="ml-1 text-xs text-gray-500">({field})</span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}

                                {/* Message si aucun champ trouvé */}
                                {directFields.length === 0 && relatedFields.length === 0 && searchField && (
                                    <div className="py-8 text-center text-sm text-gray-500">No fields found for "{searchField}"</div>
                                )}
                            </div>
                        </ScrollArea>

                        {/* Résumé */}
                        <div className="rounded border bg-muted/50 p-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">Visible fields :</span>
                                <Badge variant="secondary">{allFieldsVisible ? `All (${allFields.length})` : `${visibleFieldsCount} on ${allFields.length}`}</Badge>
                            </div>
                            {!allFieldsVisible && visibleFieldsCount > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1 text-xs text-muted-foreground">
                                    {tempFields.sort().map(field => (
                                        <Badge key={field} variant="outline" className="break-all">
                                            {field}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-end justify-end gap-4 py-4">
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button variant="default" onClick={handleValidate}>
                            Validate
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
